import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Phone,
  Star,
  Clock,
  BadgeCheck,
  Heart,
  ChevronLeft,
  ChevronRight,
  Award,
  Zap,
  Calendar,
  Shield,
} from 'lucide-react';
import type { ServiceProvider } from '@/types';
import { ServiceBadge } from './ServiceBadge';

interface ProviderCardProps {
  provider: ServiceProvider;
  distance?: number;
  onHover?: (providerId: string | null) => void;
  isHighlighted?: boolean;
  /** Compact mode for embedded map overlays */
  compact?: boolean;
}

// Helper to check if provider is currently open
function isProviderOpen(hours: ServiceProvider['hours']): boolean {
  const now = new Date();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
  const today = days[now.getDay()];
  const todayHours = hours[today];

  if (!todayHours) return false;

  const currentTime = now.getHours() * 100 + now.getMinutes();
  const openTime = parseInt(todayHours.open.replace(':', ''), 10);
  const closeTime = parseInt(todayHours.close.replace(':', ''), 10);

  return currentTime >= openTime && currentTime <= closeTime;
}

// Helper to format years in business
function formatYearsInBusiness(years: number | null): string | null {
  if (!years) return null;
  if (years === 1) return '1 year';
  if (years < 1) return 'New';
  return `${years}+ years`;
}

export function ProviderCard({
  provider,
  distance,
  onHover,
  isHighlighted,
  compact = false,
}: ProviderCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  // Combine cover image with gallery
  const allImages = [
    provider.coverImageUrl,
    ...provider.galleryUrls,
  ].filter(Boolean) as string[];

  const hasMultipleImages = allImages.length > 1;
  const isOpen = isProviderOpen(provider.hours);
  const yearsText = formatYearsInBusiness(provider.yearsInBusiness);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorited(!isFavorited);
  };

  // Compact card for map overlays
  if (compact) {
    return (
      <Link
        to={`/provider/${provider.slug}`}
        className={`
          block bg-white rounded-xl overflow-hidden transition-all duration-300
          ${isHighlighted ? 'ring-2 ring-primary-500 shadow-xl' : 'shadow-lg hover:shadow-xl'}
        `}
        onMouseEnter={() => {
          onHover?.(provider.id);
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          onHover?.(null);
          setIsHovered(false);
        }}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {allImages.length > 0 ? (
            <img
              src={allImages[currentImageIndex]}
              alt={provider.businessName}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-3xl drop-shadow-lg">
                {provider.businessName[0]}
              </span>
            </div>
          )}

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          {/* Verified badge */}
          {provider.isVerified && (
            <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-sm">
              <BadgeCheck className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[10px] font-semibold text-gray-700">Verified</span>
            </div>
          )}

          {/* Rating overlay */}
          {provider.averageRating && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm">
              <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-bold text-gray-900">{provider.averageRating.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({provider.reviewCount})</span>
            </div>
          )}

          {/* Open status */}
          <div className={`absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
            isOpen ? 'bg-green-500/90 text-white' : 'bg-gray-800/80 text-gray-200'
          }`}>
            <Clock className="w-3 h-3" />
            {isOpen ? 'Open' : 'Closed'}
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          <h3 className="font-semibold text-gray-900 truncate text-sm mb-1">
            {provider.businessName}
          </h3>

          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            {provider.city && provider.state && (
              <span className="flex items-center gap-0.5">
                <MapPin className="w-3 h-3" />
                {provider.city}
              </span>
            )}
            {distance !== undefined && (
              <span className="text-primary-600 font-medium">
                {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1">
            {provider.serviceTypes.slice(0, 2).map((type) => (
              <ServiceBadge key={type} type={type} size="sm" />
            ))}
            {provider.serviceTypes.length > 2 && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium">
                +{provider.serviceTypes.length - 2}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  // Full card - Airbnb style vertical layout
  return (
    <Link
      to={`/provider/${provider.slug}`}
      className={`
        group block bg-white rounded-2xl overflow-hidden transition-all duration-300
        ${isHighlighted ? 'ring-2 ring-primary-500 shadow-xl' : 'shadow-md hover:shadow-xl'}
      `}
      onMouseEnter={() => {
        onHover?.(provider.id);
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        onHover?.(null);
        setIsHovered(false);
      }}
    >
      {/* Image Section */}
      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
        {allImages.length > 0 ? (
          <motion.img
            key={currentImageIndex}
            src={allImages[currentImageIndex]}
            alt={provider.businessName}
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 1 }}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex items-center justify-center">
            <div className="text-center">
              <span className="text-white font-bold text-5xl drop-shadow-lg block mb-2">
                {provider.businessName[0]}
              </span>
              <span className="text-white/80 text-sm font-medium">
                {provider.serviceTypes[0]?.toUpperCase() || 'SERVICE PROVIDER'}
              </span>
            </div>
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Image navigation arrows */}
        {hasMultipleImages && isHovered && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </>
        )}

        {/* Image dots */}
        {hasMultipleImages && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {allImages.slice(0, 5).map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                className={`transition-all duration-300 rounded-full ${
                  index === currentImageIndex
                    ? 'w-2 h-2 bg-white'
                    : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/80'
                }`}
              />
            ))}
            {allImages.length > 5 && (
              <span className="text-white/80 text-[10px] ml-1">+{allImages.length - 5}</span>
            )}
          </div>
        )}

        {/* Favorite button */}
        <button
          onClick={handleFavorite}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white backdrop-blur-sm shadow-md transition-all hover:scale-110"
        >
          <Heart
            className={`w-5 h-5 transition-colors ${
              isFavorited ? 'text-red-500 fill-red-500' : 'text-gray-600'
            }`}
          />
        </button>

        {/* Top left badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {provider.isVerified && (
            <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-sm">
              <BadgeCheck className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-semibold text-gray-700">Verified Pro</span>
            </div>
          )}
          {yearsText && (
            <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-sm">
              <Award className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-gray-700">{yearsText}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-lg leading-tight group-hover:text-primary-600 transition-colors truncate">
              {provider.businessName}
            </h3>
            {provider.city && provider.state && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3.5 h-3.5" />
                {provider.city}, {provider.state}
                {distance !== undefined && (
                  <span className="text-primary-600 font-medium ml-1">
                    • {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Rating */}
          {provider.averageRating && (
            <div className="flex items-center gap-1 bg-gray-50 px-2.5 py-1.5 rounded-lg">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-bold text-gray-900">{provider.averageRating.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({provider.reviewCount})</span>
            </div>
          )}
        </div>

        {/* Service types */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {provider.serviceTypes.slice(0, 4).map((type) => (
            <ServiceBadge key={type} type={type} size="sm" />
          ))}
          {provider.serviceTypes.length > 4 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
              +{provider.serviceTypes.length - 4}
            </span>
          )}
        </div>

        {/* Specialties */}
        {provider.specialties.length > 0 && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-1">
            <span className="font-medium">Specializes in:</span>{' '}
            {provider.specialties.slice(0, 3).join(', ')}
            {provider.specialties.length > 3 && ` +${provider.specialties.length - 3} more`}
          </p>
        )}

        {/* Quick info badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            isOpen ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            <Clock className="w-3 h-3" />
            {isOpen ? 'Open Now' : 'Closed'}
          </div>

          {provider.certifications.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
              <Shield className="w-3 h-3" />
              Licensed & Insured
            </div>
          )}

          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
            <Zap className="w-3 h-3" />
            Responds quickly
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            {provider.phone && (
              <a
                href={`tel:${provider.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-50 hover:bg-primary-100 rounded-lg text-sm font-medium text-primary-700 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            )}
            <button
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Book
            </button>
          </div>

          <span className="text-sm text-primary-600 font-semibold group-hover:underline">
            View Profile →
          </span>
        </div>
      </div>
    </Link>
  );
}
